/* eslint-disable react/jsx-no-bind */
//@flow
import React from 'react';
import ReactTable from 'react-table';
import {
    Box,
    Text,
    Columns,
    Column,
    Flex,
    Button as NeoButton,
    Tooltip
} from '@locus-taxy/neo-ui';
import uuidv4 from 'uuid/v4';
import { Map } from 'immutable';
import baseFpPlanViewFactory from './baseFpPlanViewFactory';
import DefaultTableCell from 'common/components/common/DefaultTableCell';
import FpPlanViewActionsBar from './FpPlanViewActionsBar';
import FpPlanViewTable from './FpPlanViewTable';
import { FpContainers } from 'common/entity/pjpMaster/planView/FpPlanDetailsContainer';
import FpPlanMap from './FpPlanMap';
import { MarkerTypeEnum } from 'common/enums/MapEnum';
import isEqual from 'lodash/isEqual';
import { PolygonOptionTypes } from 'common/entity/pjpMaster/planView/FpAllocatedVisitsContainer';
import DifferentiableColorPicker from 'common/utils/DifferentiableColorPicker';
import { FpMapColorByOptions } from './FpPlanMapOptions';
import type { BeatVisit } from 'common/models/internal/BeatVisit';
import { type TFunction } from 'react-i18next';
import { PjpPlanStatus } from 'common/models/internal/PermanentJourneyPlan';
import { FilterCell } from './FilterCell';
import {
    PermissionMatch,
    AccessDeniedMessage,
    RoleOperationEnum
} from 'common/modules/rbac';

const FpUnallocatedVisitsView = baseFpPlanViewFactory({
    TableView: FpPlanViewTable,
    MapView: FpPlanMap,
    ActionsBar: FpPlanViewActionsBar
});

type UnallocatedColumnCellPropTypes = {
    planStatus: $Values<typeof PjpPlanStatus>,
    openMoveVisitDialog: (visitId: string) => void,
    t: TFunction,
    visit: BeatVisit
};
class UnallocateColumnCell extends React.PureComponent<UnallocatedColumnCellPropTypes> {
    allocateVisit = () => {
        const { visit, openMoveVisitDialog } = this.props;
        openMoveVisitDialog(visit.id);
    };
    render() {
        const { t, planStatus } = this.props;
        return (
            <PermissionMatch
                operations={RoleOperationEnum.EDIT_PJP}
                render={hasPermission => {
                    if (planStatus !== PjpPlanStatus.Frozen) {
                        return (
                            <Tooltip
                                shouldWrapChildren
                                tooltip={
                                    !hasPermission ? (
                                        <AccessDeniedMessage
                                            action={t(
                                                'fpPlanView.allocateVisit'
                                            )}
                                        />
                                    ) : null
                                }
                            >
                                <NeoButton
                                    icon="dispatcher_allocate"
                                    onClick={this.allocateVisit}
                                    disabled={!hasPermission}
                                >
                                    {t('fpPlanView.allocateVisit')}
                                </NeoButton>
                            </Tooltip>
                        );
                    }
                    return <Text>{t('fpPlanView.frozen')}</Text>;
                }}
            />
        );
    }
}

class UnallocatedVisitIdCell extends React.PureComponent {
    allocateVisit = () => {
        const { visit, openMoveVisitDialog } = this.props;
        openMoveVisitDialog(visit.id);
    };

    onUnallocatedVisitClick = () => {
        const { onUnallocatedVisitSelect, visit } = this.props;
        onUnallocatedVisitSelect(visit.id);
    };
    render() {
        const {
            visit: { id },
            maxEditLimitReached,
            planStatus,
            t
        } = this.props;

        return (
            <Flex alignItems="center" width="100%" height="100%">
                <Box
                    height="inherit"
                    p={2}
                    width="inherit"
                    onClick={this.onUnallocatedVisitClick}
                >
                    <Columns height="fluid" alignY="center" width="inherit">
                        <Column width="fluid" alignItems="center">
                            <Flex height="100%" alignItems="center">
                                <Text>{id}</Text>
                            </Flex>
                        </Column>

                        {planStatus !== PjpPlanStatus.Frozen ? (
                            <Column width="content">
                                <PermissionMatch
                                    operations={RoleOperationEnum.EDIT_PJP}
                                    render={hasPermission => (
                                        <Tooltip
                                            tooltip={(() => {
                                                if (!hasPermission) {
                                                    return (
                                                        <AccessDeniedMessage
                                                            action={t(
                                                                'fpPlanView.moveVisit'
                                                            )}
                                                        />
                                                    );
                                                }
                                                if (maxEditLimitReached) {
                                                    return t(
                                                        'fpPlanView.editLimitReached'
                                                    );
                                                }
                                                return t(
                                                    'fpPlanView.moveVisit'
                                                );
                                            })()}
                                            shouldWrapChildren
                                        >
                                            <NeoButton
                                                icon="add"
                                                appearance="icon"
                                                palette="primary"
                                                onClick={this.allocateVisit}
                                                disabled={
                                                    !hasPermission ||
                                                    maxEditLimitReached
                                                }
                                            />
                                        </Tooltip>
                                    )}
                                />
                            </Column>
                        ) : null}
                    </Columns>
                </Box>
            </Flex>
        );
    }
}

export default class FpUnallocatedVisitsContainer extends React.PureComponent {
    planMapWrapRef: ?typeof PlanMapWrap;
    unallocatedContainerRef: React.ElementRef<any>;

    constructor(props) {
        super(props);
        const { salesbeatPlan, plan } = props;
        const unallocatedVisits = this.getUnallocatedVisits(
            plan,
            salesbeatPlan
        );
        this.state = {
            pjPlan: props.plan,
            salesbeatPlan: props.salesbeatPlan,
            unallocatedVisits,
            paginationDetails: this._getDefaultPaginationDetails(
                unallocatedVisits
            ),
            mapOptions: this.getDefaultMapOptions(),
            isMoveVisitDialogVisible: false,
            selectedUnallocatedVisit: null,
            hoveredUnallocatedVisit: null,
            filteredVisits: unallocatedVisits,
            contextMenuOpen: false,
            contextMenu: null,
            targetVisitId: null,
            visibleBeatColorMap: this._getDefaultBeatColorMapping(
                props.salesbeatPlan.beats
            ),
            riderIdColorMap: Map(),
            weekColorMap: Map(),
            weekDayColorMap: Map(),
            salesCategoryColorMap: Map()
        };
    }

    componentDidMount() {
        const { outletSalesCategories, plan } = this.props;
        const riderIdColorMap = this.getRiderIdColorMap(plan);
        const salesCategoryColorMap = this.getSalesCategoryColorMap(
            outletSalesCategories
        );
        const weekColorMap = this._getWeekColorMap(plan);
        const weekDayColorMap = this._getWeekDayColorMap(plan);

        this.setState({
            salesCategoryColorMap,
            riderIdColorMap,
            weekColorMap,
            weekDayColorMap
        });
    }

    componentDidUpdate(prevProps) {
        if (!isEqual(this.props.salesbeatPlan, prevProps.salesbeatPlan)) {
            const { salesbeatPlan, plan } = this.props;
            const weekColorMap = this._getWeekColorMap(plan);
            const weekDayColorMap = this._getWeekDayColorMap(plan);
            const riderIdColorMap = this.getRiderIdColorMap(plan);
            const unallocatedVisits = this.getUnallocatedVisits(
                plan,
                salesbeatPlan
            );
            this.setState({
                salesbeatPlan,
                unallocatedVisits,
                filteredVisits: unallocatedVisits,
                riderIdColorMap,
                weekColorMap,
                weekDayColorMap
            });
        }
    }

    getSalesCategoryColorMap = outletSalesCategories => {
        const colorPicker = new DifferentiableColorPicker();
        return outletSalesCategories.reduce(
            (salesCategoryMap, salesCategory) => {
                if (!salesCategoryMap.has(salesCategory.id)) {
                    const updatedMap = salesCategoryMap.set(
                        salesCategory.id,
                        colorPicker.pick()
                    );
                    return updatedMap;
                }
                return salesCategoryMap;
            },
            Map()
        );
    };

    getRiderIdColorMap = pjPlan => {
        const { riderIds } = pjPlan.ridersInfo;
        const riderColorPicker = new DifferentiableColorPicker();
        const riderIdColorMap = riderIds.reduce((riderColorMap, riderId) => {
            if (!riderColorMap.has(riderId)) {
                const updatedRiderColorMap = riderColorMap.set(
                    riderId.riderId,
                    riderColorPicker.pick()
                );
                return updatedRiderColorMap;
            }
            return riderColorMap;
        }, Map());
        return riderIdColorMap;
    };

    _getWeekDayColorMap = pjpPlan => {
        const { beats } = pjpPlan.salesbeatPlan;
        const weekColorPicker = new DifferentiableColorPicker();
        const weekColorMap = beats.reduce((weekColorMap, { beatDay }) => {
            if (!weekColorMap.has(beatDay.dayOfWeek)) {
                const updatedWeekColorMap = weekColorMap.set(
                    beatDay.dayOfWeek,
                    weekColorPicker.pick()
                );
                return updatedWeekColorMap;
            }
            return weekColorMap;
        }, Map());
        return weekColorMap;
    };

    _getWeekColorMap = pjpPlan => {
        const { beats } = pjpPlan.salesbeatPlan;
        const weekColorPicker = new DifferentiableColorPicker();
        const weekColorMap = beats.reduce((weekColorMap, { beatDay }) => {
            if (!weekColorMap.has(beatDay.week)) {
                const updatedWeekColorMap = weekColorMap.set(
                    beatDay.week,
                    weekColorPicker.pick()
                );
                return updatedWeekColorMap;
            }
            return weekColorMap;
        }, Map());
        return weekColorMap;
    };

    HEADER_HEIGHT = 42;
    rtTable = React.createRef<ReactTable>();

    getAllFilters: () => Array<Object> = () => {
        return [];
    };

    _getDefaultBeatColorMapping(beats) {
        const colorPicker = new DifferentiableColorPicker();
        return beats.reduce((colorMap, beat) => {
            const updatedColorMap = colorMap.set(
                beat.beatId,
                colorPicker.pick()
            );
            return updatedColorMap;
        }, Map());
    }

    onUnallocatedVisitContextMenuOpen = (
        x: number,
        y: number,
        visitId: string
    ) => {
        const contextId = uuidv4();
        this.setState({
            contextMenuOpen: true,
            contextMenu: {
                id: contextId,
                x,
                y,
                visitId
            }
        });
        return contextId;
    };

    getSalesmanOptions = () => {
        const { pjPlan } = this.state;
        const { ridersInfo } = pjPlan;
        const { riderIds } = ridersInfo;
        const riderOptions = riderIds.map(rider => {
            return {
                value: rider.riderId,
                label: rider.riderId
            };
        });
        return riderOptions;
    };

    onOptionsChange: (updatedMapOptions: any) => void = (
        updatedMapOptions: Object
    ) => {
        this.setState({ mapOptions: updatedMapOptions });
    };

    onSearchInputChange: (value: string) => void = value => {
        const { unallocatedVisits } = this.state;
        const lowerCaseValue = value.toLowerCase();
        if (!value) {
            this.setState({
                filteredVisits: unallocatedVisits
            });
        }
        const filteredVisits = unallocatedVisits.filter(visit => {
            const beatId = visit.id.toLowerCase();
            const { category, sourceOutletId } = visit;
            return (
                beatId.toLowerCase().includes(lowerCaseValue) ||
                category.toLowerCase().includes(lowerCaseValue) ||
                sourceOutletId.toLowerCase().includes(lowerCaseValue)
            );
        });
        this.setState({
            filteredVisits
        });
    };

    getDefaultMapOptions = () => {
        return {
            markerType: MarkerTypeEnum.Dot,
            showUnassignedMarkers: true,
            showMarkers: true,
            polygon: true,
            polygonOption: PolygonOptionTypes.ALL,
            colorByType: FpMapColorByOptions.SalesCategory
        };
    };

    onEditBeat = (targetBeatId, outletVisitId) => {
        const { moveVisit } = this.props;
        moveVisit({ targetBeatId, outletVisitId });
    };

    toggleMoveVisitDialog = () => {
        const { isMoveVisitDialogVisible } = this.state;
        if (isMoveVisitDialogVisible) {
            this.closeMoveVisitDialog();
        }
        this.setState({
            targetVisitId: null
        });
    };

    closeMoveVisitDialog = () => {
        this.setState({
            isMoveVisitDialogVisible: false,
            selecselectedUnallocatedVisittedVisit: null
        });
    };

    openMoveVisitDialog = (visitId: string) => {
        this.setState({
            isMoveVisitDialogVisible: true,
            selectedUnallocatedVisit: visitId,
            targetVisitId: visitId
        });
    };

    _getDefaultPaginationDetails = unallocatedVisits => {
        const totalPages = Math.ceil(unallocatedVisits.length / 10);
        return {
            pageSize: 10,
            totalItems: unallocatedVisits.length,
            totalPages,
            selectedPage: 1,
            selectedPageIndex: 0,
            pageSizeOptions: [5, 10, 15]
        };
    };

    getUnallocatedVisits = (plan, salesbeatPlan) => {
        const { unallocatedVisitIds } = salesbeatPlan;
        const { outletVisits } = plan;
        const unallocatedVisits = unallocatedVisitIds.map(unallocatedVisit => {
            const outletData = outletVisits.find(visit => {
                return visit.id === unallocatedVisit;
            });
            const {
                id,
                sourceOutletId,
                zoneId,
                category,
                visitType,
                address,
                revenue,
                possibleDays
            } = outletData;
            return {
                id,
                sourceOutletId,
                zoneId,
                category,
                visitType,
                address,
                revenue,
                possibleDays
            };
        });
        return unallocatedVisits || [];
    };

    onUnallocatedVisitSelect = visitId => {
        this.setState({
            selectedUnallocatedVisit: visitId
        });
    };
    onUnallocatedVisitDeSelect = () => {
        this.setState({
            selectedUnallocatedVisit: null
        });
    };
    onUnallocatedVisitHover = visitId => {
        this.setState({
            hoveredUnallocatedVisit: visitId
        });
    };
    onUnallocatedVisitHoverOut = () => {
        this.setState({
            hoveredUnallocatedVisit: null
        });
    };

    getContainer = () => {
        return FpContainers.Unallocated;
    };

    getContainerRef = (ref: ?typeof PlanMapWrap) => {
        this.unallocatedContainerRef = ref;
    };

    getContainerRefValue = () => {
        return this.unallocatedContainerRef;
    };

    getPlanMapWrapRef = (ref: ?typeof PlanMapWrap) => {
        this.planMapWrapRef = ref;
    };

    _getAddressString = ({
        placeName,
        formattedAddress,
        locality,
        state,
        countryCode,
        pincode,
        city
    }) => {
        return [
            placeName,
            formattedAddress,
            locality,
            city,
            state,
            countryCode,
            pincode
        ]
            .filter(s => s)
            .join(', ');
    };

    columns = Object.freeze([
        {
            id: 'visitId',
            Header: this.props.t('fpPlanView.visitId'),
            fixed: true,
            Cell: props => {
                const { original, tdProps } = props;
                const {
                    openMoveVisitDialog,
                    onUnallocatedVisitSelect,
                    planStatus
                } = tdProps.rest;
                return (
                    <UnallocatedVisitIdCell
                        planStatus={planStatus}
                        maxEditLimitReached={this.props.maxEditLimitReached}
                        visit={original}
                        onUnallocatedVisitSelect={onUnallocatedVisitSelect}
                        openMoveVisitDialog={openMoveVisitDialog}
                        t={this.props.t}
                    />
                );
            },
            width: 300,
            accessor: 'id',
            Filter: FilterCell
        },
        {
            id: 'outletName',
            Header: this.props.t('fpPlanView.outletId'),
            accessor: 'sourceOutletId',
            Filter: FilterCell,
            width: 250
        },
        {
            id: 'salesCategory',
            Header: this.props.t('fpPlanView.salesCategory'),
            accessor: 'category',
            Filter: FilterCell,
            width: 250
        },
        {
            id: 'address',
            Header: this.props.t('fpPlanView.address'),
            accessor: ({ address }) =>
                this._getAddressString(address.locationAddress),
            Cell: props => {
                const { value } = props;
                return <DefaultTableCell value={value} />;
            },
            Filter: FilterCell,
            width: 250
        },
        {
            id: 'allocateVisit',
            Header: this.props.t('fpPlanView.allocateVisit'),
            Cell: props => {
                const { original, tdProps } = props;
                const { openMoveVisitDialog, t, planStatus } = tdProps.rest;
                return (
                    <UnallocateColumnCell
                        t={t}
                        planStatus={planStatus}
                        visit={original}
                        openMoveVisitDialog={openMoveVisitDialog}
                    />
                );
            },
            sortable: false,
            Filter: false,
            filterable: false,
            width: 250
        }
    ]);

    onPageChange = (nextPageIndex: number): void => {
        const { paginationDetails } = this.state;
        this.setState({
            paginationDetails: {
                ...paginationDetails,
                selectedPage: nextPageIndex + 1,
                selectedPageIndex: nextPageIndex
            }
        });
    };

    onPageSizeChange = (nextPageSize: number): void => {
        const { paginationDetails, unallocatedVisits } = this.state;
        const nextPaginationDetails = {
            ...paginationDetails,
            selectedPage: 1,
            selectedPageIndex: 0,
            pageSize: nextPageSize,
            totalPages: Math.ceil(unallocatedVisits.length / nextPageSize)
        };
        this.setState({
            paginationDetails: nextPaginationDetails
        });
    };

    render() {
        const {
            filteredVisits,
            paginationDetails,
            mapOptions,
            isMoveVisitDialogVisible,
            selectedUnallocatedVisit,
            hoveredUnallocatedVisit,
            contextMenu,
            targetVisitId,
            pjPlan,
            visibleBeatColorMap,
            riderIdColorMap,
            salesCategoryColorMap,
            weekColorMap,
            weekDayColorMap
        } = this.state;
        const beats = this.props.salesbeatPlan?.beats || [];
        return (
            <FpUnallocatedVisitsView
                {...this.props}
                getAllFilters={this.getAllFilters}
                beats={beats}
                visibleBeats={beats}
                filteredBeats={beats}
                riderIdColorMap={riderIdColorMap}
                weekColorMap={weekColorMap}
                weekDayColorMap={weekDayColorMap}
                salesCategoryColorMap={salesCategoryColorMap}
                visibleBeatColorMap={visibleBeatColorMap}
                planStatus={pjPlan.planStatus}
                isMoveVisitDialogVisible={isMoveVisitDialogVisible}
                getSalesmanOptions={this.getSalesmanOptions}
                closeMoveVisitDialog={this.closeMoveVisitDialog}
                toggleMoveVisitDialog={this.toggleMoveVisitDialog}
                openMoveVisitDialog={this.openMoveVisitDialog}
                columns={this.columns}
                mapOptions={mapOptions}
                getContainer={this.getContainer}
                paginationDetails={paginationDetails}
                data={filteredVisits}
                unallocatedVisits={filteredVisits}
                pjPlan={pjPlan}
                onEditBeat={this.onEditBeat}
                getContainerRef={this.getContainerRef}
                getContainerRefValue={this.getContainerRefValue}
                getPlanMapWrapRef={this.getPlanMapWrapRef}
                onOptionsChange={this.onOptionsChange}
                onSearchInputChange={this.onSearchInputChange}
                onUnallocatedVisitSelect={this.onUnallocatedVisitSelect}
                onUnallocatedVisitDeselect={this.onUnallocatedVisitDeSelect}
                onUnallocatedVisitHover={this.onUnallocatedVisitHover}
                onUnallocatedVisitHoverOut={this.onUnallocatedVisitHoverOut}
                onVisitDeselect={this.onUnallocatedVisitDeSelect}
                selectedUnallocatedVisit={selectedUnallocatedVisit}
                hoveredUnallocatedVisit={hoveredUnallocatedVisit}
                contextMenu={contextMenu}
                onUnallocatedVisitContextMenuOpen={
                    this.onUnallocatedVisitContextMenuOpen
                }
                targetVisit={targetVisitId}
                onPageChange={this.onPageChange}
                onPageSizeChange={this.onPageSizeChange}
            />
        );
    }
}
